import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, forkJoin, map } from 'rxjs';
import Stripe from 'stripe';
import { freeStripeProductPrice } from '../billing-consts';
import { GROWTHBOOK } from '../growthbook';
import { AppConfigService } from '../providers/config.service';
import { Product, RecurringInterval } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { TransferStateService } from './transfer-state.service';

export type PriceDetails = {
  basePlan: number;
  storageAddonCost: number;
  subTotal: number;
  yearlyDiscount: number;
  promoDiscount: number;
  totalDiscount: number;
  balance: number;
  total: number;
};

export interface PriceDetailsCalculation {
  monthlyAmount: number;
  monthlyAmountWithoutDiscount: number;
  yearlyAmount: number;
  yearlyAmountWithoutDiscount: number;
  monthlyDiscountAmount: number;
  displayAmountWhole: number;
  displayAmountCents: number;
}

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  private growthBook = inject(GROWTHBOOK);
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private transferState = inject(TransferStateService);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);

  public getPrices(product_type: 'main' | 'storage_addon' | 'seat_addon') {
    return this.loader
      .get('prices', () =>
        forkJoin([this.getPricesInternal('year'), this.getPricesInternal('month')]).pipe(
          map(resp => resp.flat()),
          map(prices => prices.filter(price => price.recurring.interval_count === 1)),
          map(prices => (product_type === 'main' ? prices.filter(price => price.lookup_key) : prices))
        )
      )
      .pipe(
        map(prices => prices.filter(price => (price.product as Stripe.Product).metadata.picflow_type === product_type))
      );
  }

  private getPricesInternal(recurring_interval: 'month' | 'year') {
    const url = `${this.config.apiEndpoint}/billing/prices?limit=100&recurring_interval=${recurring_interval}`;
    return this.transferState.withStateTransferObservable(`prices_${recurring_interval}`, () =>
      this.http.get<{ data: Stripe.Price[] }>(url).pipe(map(resp => resp.data))
    );
  }

  /**
   * Returns the monthly price of the product with yearly billing cycle.
   */
  public async getPlanPrice(product: Product) {
    const prices = await firstValueFrom(this.getPrices('main'));
    const price = prices.find(p => p.product === product.id && p.recurring.interval === 'year');
    return price.unit_amount / 100 / 12;
  }

  private calcAproxYearlyToMonthlyPriceAmount(yearlyAmount: number) {
    return yearlyAmount / 0.8 / 12;
  }

  private getMatchingMonthlyPrice(yearlyPrice: Stripe.Price, prices: Stripe.Price[]): Stripe.Price {
    const aproxYearlyToMonthlyPriceAmount = this.calcAproxYearlyToMonthlyPriceAmount(yearlyPrice.unit_amount);
    const relevantMonthlyPrices = prices.filter(
      price =>
        price.recurring.interval === 'month' &&
        (price.product as Stripe.Product).id === (yearlyPrice.product as Stripe.Product).id
    );
    let closestPrice: Stripe.Price;
    let closestDiff = Infinity;

    for (const price of relevantMonthlyPrices) {
      const diff = Math.abs(price.unit_amount - aproxYearlyToMonthlyPriceAmount);
      if (diff < closestDiff) {
        closestPrice = price;
        closestDiff = diff;
      }
    }

    return closestPrice;
  }

  public async calcPriceDetails(price: Stripe.Price, promo?: Promotion, balance: number = 0): Promise<PriceDetails> {
    const [mainPrices, storageAddonPrices] = await Promise.all([
      firstValueFrom(this.getPrices('main')),
      firstValueFrom(this.getPrices('storage_addon')),
    ]);
    const mainPrice = mainPrices.find(_price => _price.id === price.id);
    const storageAddonPrice = this.getDefaultPrice(mainPrice.recurring.interval, storageAddonPrices);
    const storageAddon = this.state.plan.storageAddon;

    const monthlyPrice =
      mainPrice.recurring.interval === 'month' ? mainPrice : this.getMatchingMonthlyPrice(mainPrice, mainPrices);

    const basePlan = monthlyPrice.unit_amount * (price.recurring.interval === 'year' ? 12 : 1);
    const basePlanReal = mainPrice.unit_amount;
    const yearlyDiscount = basePlan - basePlanReal;
    const promoDiscount = this.getPromoDiscount(price, promo) * 100;

    const storageAddonCost = storageAddon ? storageAddon.quantity * storageAddonPrice.unit_amount : 0;

    const subTotal = basePlan + storageAddonCost;
    const totalDiscount = yearlyDiscount + promoDiscount;
    let total = basePlanReal + storageAddonCost - promoDiscount;
    if (total < 0) total = 0;

    let balanceThatWillBeUsed = 0;
    if (total > 0) {
      if (total > balance) {
        total -= balance;
        balanceThatWillBeUsed = balance;
      } else {
        balanceThatWillBeUsed = total;
        total = 0;
      }
    }

    return {
      basePlan: basePlan / 100,
      storageAddonCost: storageAddonCost / 100,
      subTotal: subTotal / 100,
      yearlyDiscount: yearlyDiscount / 100,
      promoDiscount: promoDiscount / 100,
      totalDiscount: totalDiscount / 100,
      balance: balanceThatWillBeUsed / 100,
      total: total / 100,
    };
  }

  private getPromoDiscount(price: Stripe.Price, promo: Promotion): number {
    if (!promo) return 0;
    if (promo.amountOff) return promo.amountOff;
    return (price.unit_amount / 100 / 100) * promo.percentOff;
  }

  public isDowngrade(fromPrice: Stripe.Price, to: Product) {
    const newMonthlyPrice =
      to.prices.find(p => p.recurring.interval === fromPrice.recurring.interval) || freeStripeProductPrice;
    return newMonthlyPrice.unit_amount / 100 < fromPrice.unit_amount / 100;
  }

  public isUpgrade(fromPrice: Stripe.Price, to: Product) {
    return !this.isDowngrade(fromPrice, to);
  }

  public getTrialLengthDays() {
    return this.growthBook.getFeatureValue('trial-length', 14);
  }

  getPriceDetails(
    prices: Stripe.Price[],
    promo: Promotion,
    billingFrequency: RecurringInterval
  ): PriceDetailsCalculation {
    const monthlyPrice = prices.find(price => price.recurring.interval === 'month') || freeStripeProductPrice;
    const yearlyPrice = prices.find(price => price.recurring.interval === 'year') || freeStripeProductPrice;

    let monthlyDiscountAmount = 0;
    let yearlyDiscountAmount = 0;
    if (promo?.percentOff) {
      monthlyDiscountAmount = (monthlyPrice.unit_amount / 100 / 100) * promo.percentOff;
      yearlyDiscountAmount = (yearlyPrice.unit_amount / 100 / 100) * promo.percentOff;
    }
    // Not showing fixed discount amounts here on purpose.

    const monthlyAmountWithoutDiscount = monthlyPrice.unit_amount / 100;
    const yearlyAmountWithoutDiscount = yearlyPrice.unit_amount / 100;
    const monthlyAmount = monthlyPrice.unit_amount / 100 - monthlyDiscountAmount;
    const yearlyAmount = yearlyPrice.unit_amount / 100 - yearlyDiscountAmount;

    const displayAmount = billingFrequency === 'year' ? yearlyAmount / 12 : monthlyAmount;
    const displayAmountWhole = Math.floor(displayAmount);
    const displayAmountCents = Math.round((displayAmount - displayAmountWhole) * 100);

    return {
      monthlyAmount,
      monthlyAmountWithoutDiscount,
      yearlyAmount,
      yearlyAmountWithoutDiscount,
      monthlyDiscountAmount,
      displayAmountWhole,
      displayAmountCents,
    };
  }

  public getDefaultPrice(billingFrequency: Stripe.Price.Recurring.Interval, pricesForCorrectType: Stripe.Price[] = []) {
    const defaultPriceKeys: string[] = this.growthBook.getFeatureValue('default-price-keys', []);
    return pricesForCorrectType.find(
      price => price.recurring.interval === billingFrequency && defaultPriceKeys.includes(price.lookup_key)
    );
  }

  public canStartTrial() {
    const trialLength = this.getTrialLengthDays();
    if (!this.state.activeTenant) return false;
    return !this.state.activeTenant.trial_started_at && !!trialLength;
  }
}
